import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../../App';
import { coverUrl, COVER_WIDTH } from '../../lib/coverUrl';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from './WriterOverview';
import { Download, Eye, Upload, Loader2, FileText, X, RefreshCw, Pencil, Image as ImageIcon, Trash2 } from 'lucide-react';

const MySubmissions = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [faultList, setFaultList] = useState(null);
  const [correctionFile, setCorrectionFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reloadBook, setReloadBook] = useState(null);
  const [reloadFile, setReloadFile] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [editNameBook, setEditNameBook] = useState(null);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [coverBook, setCoverBook] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [savingCover, setSavingCover] = useState(false);
  const [deleteBook, setDeleteBook] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleSaveName = async (bookId) => {
    if (!newName.trim()) { toast.error('Boeknaam mag nie leeg wees nie'); return; }
    setSavingName(true);
    try {
      await axios.put(`${API}/books/${bookId}/writer-edit`, { boek_naam: newName.trim() });
      toast.success('Boeknaam opgedateer!');
      setEditNameBook(null);
      setNewName('');
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boeknaam opdateer nie');
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveCover = async (bookId) => {
    if (!coverFile) { toast.error('Kies eers \'n voorblad'); return; }
    setSavingCover(true);
    const fd = new FormData();
    fd.append('cover_image', coverFile);
    try {
      await axios.post(`${API}/books/${bookId}/writer-cover`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Voorblad opgedateer!');
      setCoverBook(null);
      setCoverFile(null);
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie voorblad opdateer nie', { duration: 8000 });
    } finally {
      setSavingCover(false);
    }
  };

  const handleDeleteBook = async (bookId) => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/books/${bookId}/writer-delete`);
      toast.success(res.data?.message || 'Boek verwyder');
      setDeleteBook(null);
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boek verwyder nie');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/books/my-submissions`);
      setBooks(response.data);
    } catch (error) {
      toast.error('Kon nie indienings laai nie');
    } finally {
      setLoading(false);
    }
  };

  const fetchFaultList = async (bookId) => {
    try {
      const response = await axios.get(`${API}/books/${bookId}/fault-list`);
      setFaultList(response.data);
    } catch (error) {
      toast.error('Kon nie foute lys laai nie');
    }
  };

  const handleUploadCorrection = async (bookId) => {
    if (!correctionFile) {
      toast.error('Kies eers \'n file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('corrected_file', correctionFile);

    try {
      await axios.post(`${API}/books/${bookId}/upload-correction`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Regstellings suksesvol opgelaai!');
      setCorrectionFile(null);
      setSelectedBook(null);
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Oplaai het misluk');
    } finally {
      setUploading(false);
    }
  };

  const handleReloadFile = async (bookId) => {
    if (!reloadFile) {
      toast.error('Kies eers \'n boek lêer');
      return;
    }
    setReloading(true);
    const formData = new FormData();
    formData.append('book_file', reloadFile);
    try {
      await axios.post(`${API}/books/${bookId}/reload-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Boek lêer suksesvol herlaai!');
      setReloadFile(null);
      setReloadBook(null);
      fetchBooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Herlaai het misluk');
    } finally {
      setReloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          My Indienings
        </h2>
        <p className="text-gray-600">Bestuur jou boek indienings</p>
      </div>

      {books.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 text-lg mb-4">Jy het nog geen boeke ingedien nie</p>
            <Button onClick={() => window.location.href = '/writer/submit'}>
              Dien Jou Eerste Boek In
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {books.map((book) => (
            <Card key={book.id} className="hover:shadow-lg transition">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <img
                      src={coverUrl(book.id, COVER_WIDTH.default)}
                      alt={book.boek_naam}
                      loading="lazy"
                      width={128}
                      height={192}
                      className="w-32 h-48 object-cover rounded-lg shadow-md"
                    />
                  </div>
                  
                  <div className="flex-1 space-y-4">
                    <div>
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{book.boek_naam}</h3>
                          <p className="text-gray-600">deur {book.skrywer_naam}</p>
                        </div>
                        <StatusBadge status={book.status} />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Genre</p>
                        <p className="font-semibold">{book.genre}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Woorde</p>
                        <p className="font-semibold">{(book.word_count || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Bladsye</p>
                        <p className="font-semibold">{book.page_count}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Muntstukke</p>
                        <p className="font-semibold">{book.coin_value}</p>
                      </div>
                    </div>

                    {book.status === 'Wag vir Regmaak' && (
                      <div
                        data-testid="fault-list-callout"
                        className="flex items-start gap-3 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg"
                      >
                        <FileText className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-bold text-orange-900">
                            Die proefleser het ’n nuwe foutelys vir hierdie boek gestuur
                            {(book.approved_fault_count || book.fault_count_submitted)
                              ? ` (${book.approved_fault_count || book.fault_count_submitted} foute)`
                              : ''}.
                          </p>
                          <p className="text-orange-800">
                            Klik <strong>“Sien Foute Lys”</strong> of <strong>“Laai Foutelys Af”</strong> hier onder,
                            maak die regstellings, en laai dan jou regstelde boek weer op met <strong>“Laai Regstelling Op”</strong>.
                            Elke keer wat jy weer indien, wys hierdie lys die <strong>nuutste</strong> foute.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <Button
                        data-testid="download-book-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`${BACKEND_URL}/api/files/book/${book.id}?version=original`, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Laai Boek Af
                      </Button>

                      {/* Re-upload just the file (no metadata) — works in any non-published status */}
                      {!['Gepubliseer', 'Gereed vir Publikasie'].includes(book.status) && (
                        <Dialog
                          open={reloadBook?.id === book.id}
                          onOpenChange={(open) => {
                            if (!open) { setReloadBook(null); setReloadFile(null); }
                          }}
                        >
                          <DialogTrigger asChild>
                            <Button
                              data-testid="reload-file-btn"
                              variant="outline"
                              size="sm"
                              className="border-blue-400 text-blue-700 hover:bg-blue-50"
                              onClick={() => setReloadBook(book)}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Herlaai Lêer
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Herlaai Boek Lêer</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <p className="text-sm text-gray-600">
                                Vervang net die boek lêer (PDF, DOCX of EPUB) vir{' '}
                                <strong>&ldquo;{book.boek_naam}&rdquo;</strong>. Jou boek-besonderhede,
                                voorblad en status bly presies dieselfde.
                              </p>
                              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition">
                                <input
                                  id={`reload-input-${book.id}`}
                                  data-testid="reload-file-input"
                                  type="file"
                                  accept=".pdf,.docx,.epub,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/epub+zip"
                                  onChange={(e) => setReloadFile(e.target.files[0])}
                                  className="hidden"
                                />
                                <label htmlFor={`reload-input-${book.id}`} className="cursor-pointer flex flex-col items-center">
                                  <FileText className="h-12 w-12 text-gray-400 mb-2" />
                                  {reloadFile ? (
                                    <span className="text-sm text-gray-900 font-semibold">{reloadFile.name}</span>
                                  ) : (
                                    <>
                                      <span className="text-sm text-gray-600">Klik om lêer te kies</span>
                                      <span className="text-xs text-gray-400">PDF, DOCX of EPUB</span>
                                    </>
                                  )}
                                </label>
                              </div>
                              <Button
                                data-testid="reload-file-confirm-btn"
                                onClick={() => handleReloadFile(book.id)}
                                disabled={!reloadFile || reloading}
                                className="w-full bg-blue-600 hover:bg-blue-700"
                              >
                                {reloading ? (
                                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig om te herlaai...</>
                                ) : (
                                  <><RefreshCw className="h-4 w-4 mr-2" />Herlaai Lêer</>
                                )}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}

                      {/* Edit name + cover (any phase before publication) */}
                      {!['Gepubliseer', 'Gereed vir Publikasie'].includes(book.status) && (
                        <>
                          <Button
                            data-testid="edit-name-btn"
                            variant="outline"
                            size="sm"
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            onClick={() => { setEditNameBook(book); setNewName(book.boek_naam); }}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Wysig Naam
                          </Button>
                          <Button
                            data-testid="edit-cover-btn"
                            variant="outline"
                            size="sm"
                            className="border-pink-300 text-pink-700 hover:bg-pink-50"
                            onClick={() => { setCoverBook(book); setCoverFile(null); }}
                          >
                            <ImageIcon className="h-4 w-4 mr-2" />
                            Verander Voorblad
                          </Button>
                        </>
                      )}

                      {/* Delete submission (any time before published) */}
                      {book.status !== 'Gepubliseer' && (
                        <Button
                          data-testid="delete-book-btn"
                          variant="outline"
                          size="sm"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteBook(book)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Verwyder
                        </Button>
                      )}

                      {book.status === 'Wag vir Regmaak' && (
                        <>
                          <Button
                            data-testid="download-fault-list-btn"
                            variant="outline"
                            size="sm"
                            className="border-orange-300 text-orange-700 hover:bg-orange-50"
                            onClick={() => window.open(
                              `${BACKEND_URL}/api/books/${book.id}/fault-list/download?token=${encodeURIComponent(localStorage.getItem('token') || '')}`,
                              '_blank'
                            )}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Laai Foutelys Af
                          </Button>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                data-testid="view-fault-list-btn"
                                variant="outline"
                                size="sm"
                                onClick={() => fetchFaultList(book.id)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Sien Foute Lys
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Foute Lys vir &ldquo;{book.boek_naam}&rdquo;</DialogTitle>
                              </DialogHeader>
                              {faultList?.fault_entries ? (
                                <div className="space-y-4">
                                  {/* Admin Rejection Reason */}
                                  {faultList.admin_notes && (
                                    <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500 mb-2">
                                      <h4 className="font-bold text-red-900 mb-2 flex items-center">
                                        <X className="h-5 w-5 mr-2" />
                                        Rede vir Terugstuur
                                      </h4>
                                      <p className="text-red-800 whitespace-pre-wrap">{faultList.admin_notes}</p>
                                    </div>
                                  )}

                                  <p className="text-sm text-gray-600">
                                    {faultList.fault_entries.length} {faultList.fault_entries.length === 1 ? 'fout' : 'foute'} gevind.
                                    Werk asseblief deur elke ry en laai dan jou regstelde boek op.
                                  </p>

                                  <div className="overflow-x-auto border rounded-lg">
                                    <table className="w-full text-xs sm:text-sm" data-testid="writer-fault-list-table">
                                      <thead>
                                        <tr className="bg-slate-50 border-b text-left text-gray-700">
                                          <th className="py-2 px-2 font-semibold w-10">#</th>
                                          <th className="py-2 px-2 font-semibold">Hoofstuk</th>
                                          <th className="py-2 px-2 font-semibold w-12">Bls</th>
                                          <th className="py-2 px-2 font-semibold w-14">Par</th>
                                          <th className="py-2 px-2 font-semibold w-12">Sin</th>
                                          <th className="py-2 px-2 font-semibold">Tipe</th>
                                          <th className="py-2 px-2 font-semibold">Kategorie</th>
                                          <th className="py-2 px-2 font-semibold">Oorspronklike Teks</th>
                                          <th className="py-2 px-2 font-semibold">Voorgestelde Regstelling</th>
                                          <th className="py-2 px-2 font-semibold">Nota</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {faultList.fault_entries.map((entry, index) => (
                                          <tr
                                            key={`fault-${entry.bladsy}-${entry.paragraaf}-${index}`}
                                            className="border-b hover:bg-orange-50 align-top"
                                          >
                                            <td className="py-2 px-2 font-bold text-orange-700">{index + 1}</td>
                                            <td className="py-2 px-2">{entry.hoofstuk || '-'}</td>
                                            <td className="py-2 px-2">{entry.bladsy}</td>
                                            <td className="py-2 px-2">{entry.paragraaf}</td>
                                            <td className="py-2 px-2">{entry.sin}</td>
                                            <td className="py-2 px-2">{entry.fout_tipe}</td>
                                            <td className="py-2 px-2">{entry.fout_kategorie || 'Oorspronklik'}</td>
                                            <td className="py-2 px-2 max-w-[260px] break-words text-red-700">{entry.oorspronklike_teks}</td>
                                            <td className="py-2 px-2 max-w-[260px] break-words text-emerald-700">{entry.voorgestelde_regstelling}</td>
                                            <td className="py-2 px-2 max-w-[260px] break-words text-gray-600">{entry.nota || '-'}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-gray-500">Geen foute lys beskikbaar nie</p>
                              )}
                            </DialogContent>
                          </Dialog>

                          <Dialog open={selectedBook?.id === book.id} onOpenChange={(open) => !open && setSelectedBook(null)}>
                            <DialogTrigger asChild>
                              <Button
                                data-testid="upload-correction-btn"
                                size="sm"
                                className="bg-orange-500 hover:bg-orange-600"
                                onClick={() => setSelectedBook(book)}
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                Laai Regestelde boek op
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Laai Regstellings Op</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-400 transition">
                                  <input
                                    id="correction-file"
                                    data-testid="correction-file-input"
                                    type="file"
                                    accept=".pdf,.docx,.epub,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/epub+zip"
                                    onChange={(e) => setCorrectionFile(e.target.files[0])}
                                    className="hidden"
                                  />
                                  <label htmlFor="correction-file" className="cursor-pointer flex flex-col items-center">
                                    <FileText className="h-12 w-12 text-gray-400 mb-2" />
                                    {correctionFile ? (
                                      <span className="text-sm text-gray-900 font-semibold">{correctionFile.name}</span>
                                    ) : (
                                      <>
                                        <span className="text-sm text-gray-600">Klik om regstellings op te laai</span>
                                        <span className="text-xs text-gray-400">PDF, DOCX of EPUB</span>
                                      </>
                                    )}
                                  </label>
                                </div>
                                <Button
                                  data-testid="submit-correction-btn"
                                  className="w-full bg-orange-500 hover:bg-orange-600"
                                  onClick={() => handleUploadCorrection(book.id)}
                                  disabled={uploading || !correctionFile}
                                >
                                  {uploading ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Besig...
                                    </>
                                  ) : (
                                    'Dien Regstellings In'
                                  )}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit book name dialog */}
      <Dialog open={!!editNameBook} onOpenChange={(o) => { if (!o) { setEditNameBook(null); setNewName(''); } }}>
        <DialogContent data-testid="edit-name-dialog">
          <DialogHeader>
            <DialogTitle>Wysig Boeknaam</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name-input">Nuwe boeknaam</Label>
              <Input
                id="edit-name-input"
                data-testid="edit-name-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Boeknaam"
                maxLength={200}
              />
            </div>
            <Button
              data-testid="edit-name-save-btn"
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={savingName}
              onClick={() => handleSaveName(editNameBook.id)}
            >
              {savingName ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Stoor...</> : 'Stoor Naam'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change cover dialog */}
      <Dialog open={!!coverBook} onOpenChange={(o) => { if (!o) { setCoverBook(null); setCoverFile(null); } }}>
        <DialogContent data-testid="edit-cover-dialog">
          <DialogHeader>
            <DialogTitle>Verander Voorblad</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 text-sm text-pink-800">
              Die voorblad moet presies <strong>1024×1536 pixels</strong> wees (portret, 2:3 verhouding).
              Enige ander grootte word verwerp sodat die titel en prent nie afgesny word nie.
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-pink-400 transition">
              <input
                id="cover-input"
                data-testid="edit-cover-input"
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                onChange={(e) => setCoverFile(e.target.files[0])}
                className="hidden"
              />
              <label htmlFor="cover-input" className="cursor-pointer flex flex-col items-center">
                <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
                {coverFile ? (
                  <span className="text-sm text-gray-900 font-semibold">{coverFile.name}</span>
                ) : (
                  <>
                    <span className="text-sm text-gray-600">Klik om voorblad te kies</span>
                    <span className="text-xs text-gray-400">JPG of PNG — presies 1024×1536</span>
                  </>
                )}
              </label>
            </div>
            <Button
              data-testid="edit-cover-save-btn"
              className="w-full bg-pink-600 hover:bg-pink-700"
              disabled={!coverFile || savingCover}
              onClick={() => handleSaveCover(coverBook.id)}
            >
              {savingCover ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Laai op...</> : 'Stoor Voorblad'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteBook} onOpenChange={(o) => { if (!o) setDeleteBook(null); }}>
        <DialogContent data-testid="delete-book-dialog">
          <DialogHeader>
            <DialogTitle>Verwyder Indiening</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Is jy seker jy wil <strong>&ldquo;{deleteBook?.boek_naam}&rdquo;</strong> permanent verwyder?
              Hierdie aksie kan nie ongedaan gemaak word nie.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteBook(null)} data-testid="delete-cancel-btn">
                Kanselleer
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-confirm-btn"
                disabled={deleting}
                onClick={() => handleDeleteBook(deleteBook.id)}
              >
                {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verwyder...</> : 'Ja, verwyder'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default MySubmissions;